import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import Keycloak, { KeycloakLoginOptions, KeycloakLogoutOptions } from 'keycloak-js'
import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react'
import httpService, { api } from '../../services/HttpService'
import { extranetUsersQueryKeyFactory } from '../users/users.queries'
import { AuthenticationContext } from './AuthenticationContext'
import { createUserData, initializeKeycloak } from './authentication.utils'
import { AuthenticatedUserDto, KeycloakParsedToken } from './dto'

const __CUSTOMER_SERVICE_ACL =
  import.meta.env['VITE_PHARMAC_CUSTOMER_SERVICE_ACL'] || 'pharmac-extranet-customer-service'
const __USER_ACL = import.meta.env['VITE_PHARMAC_USER_ACL'] || 'pharmac-extranet-user'

const AuthenticationProvider = ({ children }: PropsWithChildren) => {
  const [keycloakInstance, setKeycloakInstance] = useState<Keycloak | undefined>()
  const [user, setUser] = useState<AuthenticatedUserDto | undefined>(undefined)
  const [isInitialized, setIsInitialized] = useState<boolean>(() => !!keycloakInstance)
  const [isConfigured, setIsConfigured] = useState<boolean>(false)

  const initEffect = async () => {
    try {
      const keycloak = await initializeKeycloak()

      if (!keycloak.authenticated) {
        console.log('User is not authenticated')
      }

      setKeycloakInstance(keycloak)
    } catch (error) {
      console.error('Keycloak initialization error', error)
    } finally {
      setIsInitialized(true)
    }
  }

  useEffect(() => {
    initEffect()
  }, [])

  const userQuery = useQuery({
    queryKey: extranetUsersQueryKeyFactory.getCurrentUser(),
    queryFn: async () => await api.users.getLoggedInUser(),
    enabled: isConfigured && keycloakInstance?.authenticated,
  })

  useEffect(() => {
    if (keycloakInstance && keycloakInstance.tokenParsed) {
      httpService.configure(keycloakInstance)
      setIsConfigured(true)
      setIsInitialized(true)
    }
  }, [keycloakInstance])

  useEffect(() => {
    if (
      userQuery.data &&
      !userQuery.isLoading &&
      keycloakInstance &&
      keycloakInstance.tokenParsed
    ) {
      setUser(createUserData(keycloakInstance.tokenParsed as KeycloakParsedToken, userQuery.data))
    }
  }, [userQuery.data, userQuery.isLoading])

  const userHasRole = useCallback(
    (role: string) => {
      if (!keycloakInstance || !user) return false

      return keycloakInstance.hasResourceRole(role, import.meta.env['VITE_KEYCLOAK_CLIENT_ID'])
    },
    [keycloakInstance, user]
  )

  const handleLogout = useCallback(async (keycloak?: Keycloak, options?: KeycloakLogoutOptions) => {
    if (keycloak) {
      await keycloak.logout(options)
    }
    httpService.unconfigure()
  }, [])

  const handleConsumeToken = useCallback(async (token: string) => {
    const result = await api.customers.consumeInviteToken(token)
    return result
  }, [])

  const getResetPasswordUrl = useCallback(
    async (locale: 'fi' | 'en' | 'sv' = 'fi') => {
      if (!keycloakInstance) return ''
      return await keycloakInstance.createLoginUrl({
        redirectUri: window.location.href,
        action: 'UPDATE_PASSWORD',
        prompt: 'login',
        locale,
      })
    },
    [keycloakInstance]
  )

  const authenticationContext = useMemo<AuthenticationContext>(
    () => ({
      user,
      isPharmacUser: !!user && userHasRole(__USER_ACL),
      isPharmacCustomerService: !!user && userHasRole(__CUSTOMER_SERVICE_ACL),
      isLoggedIn: !!keycloakInstance && !!user,
      doLogout: async (options?: KeycloakLogoutOptions) => handleLogout(keycloakInstance, options),
      doLogin: async (options?: KeycloakLoginOptions) => keycloakInstance?.login(options),
      getChangePasswordUrl: getResetPasswordUrl,
      doConsumeToken: handleConsumeToken,
      userHasRole,
      updateToken: async () => keycloakInstance?.updateToken(20) ?? false,
      hasInitialized: !!keycloakInstance && !userQuery.isLoading,
      hasAcceptedPrivacyPolicy: !!user?.privacyPolicyApprovalDate,
      hasAcceptedTermsAndConditions: !!user?.extranetTermsOfUseApprovalDate,
    }),
    [keycloakInstance, user]
  )

  if (!isInitialized) return null
  if (isInitialized && !keycloakInstance) return <>keycloak timed out</>

  console.log(format(new Date(), 'hh:mm:ss'), 'AuthenticationProvider :: DEBUG ::', {
    user,
    keycloakInstance,
  })

  return (
    <AuthenticationContext.Provider value={authenticationContext}>
      {children}
    </AuthenticationContext.Provider>
  )
}

export { AuthenticationContext, AuthenticationProvider }
